import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAdminStats, getAdminAgencies, toggleAgencyStatus,
  getPendingUsers, approveUser, rejectUser, getAdminAudits
} from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [audits, setAudits] = useState([]);
  const [auditFilters, setAuditFilters] = useState({ agencyId: '', clientAgency: '', sortBy: 'date_desc' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getAdminStats(), getAdminAgencies(), getPendingUsers()])
      .then(([statsRes, agenciesRes, pendingRes]) => {
        setStats(statsRes.data);
        setAgencies(agenciesRes.data.agencies || []);
        setPendingUsers(pendingRes.data.users || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadAudits = useCallback(() => {
    getAdminAudits(auditFilters)
      .then(res => setAudits(res.data.audits || []))
      .catch(console.error);
  }, [auditFilters]);

  useEffect(() => {
    if (activeTab === 'audits') loadAudits();
  }, [activeTab, loadAudits]);

  const handleToggle = async (agencyId, currentStatus) => {
    try {
      await toggleAgencyStatus(agencyId, !currentStatus);
      setAgencies(agencies.map(a =>
        a.id === agencyId ? { ...a, is_active: !currentStatus } : a
      ));
    } catch (err) {
      alert('Failed to update agency status');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      // Refresh stats
      const statsRes = await getAdminStats();
      setStats(statsRes.data);
    } catch (err) {
      alert('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user? They will not be able to log in.')) return;
    try {
      await rejectUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (err) {
      alert('Failed to reject user');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Loading admin data...</p></div>;
  }

  const chartData = stats?.recentAudits?.map(a => ({
    name: new Date(a.created_at).toLocaleDateString(),
    score: a.compliance_score || 0,
    agency: a.agency_name,
  })) || [];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'approvals', label: `Approvals${pendingUsers.length > 0 ? ` (${pendingUsers.length})` : ''}` },
    { id: 'agencies', label: 'Agencies' },
    { id: 'audits', label: 'All Audits' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Admin Dashboard</h1>
      <p className="text-gray-500 mb-6">Platform-wide management</p>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-8 bg-gray-100 rounded-lg p-1 w-fit">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              activeTab === tab.id
                ? 'bg-white shadow text-slate-800'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm text-gray-500">Active Agencies</p>
              <p className="text-3xl font-bold text-slate-800 mt-1">{stats?.agencies || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm text-gray-500">Total Audits</p>
              <p className="text-3xl font-bold text-slate-800 mt-1">{stats?.audits?.total || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm text-gray-500">Avg Score</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{Math.round(stats?.audits?.avg_score || 0)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm text-gray-500">Total Critical</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{stats?.audits?.total_critical || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <p className="text-sm text-gray-500">Pending Approvals</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{pendingUsers.length}</p>
            </div>
          </div>

          {chartData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Recent Audit Scores</h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="score" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      {/* APPROVALS TAB */}
      {activeTab === 'approvals' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-slate-800">Pending User Approvals</h2>
            <p className="text-sm text-gray-500 mt-1">New registrations that need your review</p>
          </div>
          {pendingUsers.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400">
              <p className="text-4xl mb-3">&#10003;</p>
              <p className="font-medium">No pending approvals</p>
              <p className="text-sm mt-1">All user registrations have been reviewed.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-500 font-medium">
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Email</th>
                  <th className="px-6 py-3">Organization</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Registered</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pendingUsers.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-slate-800">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{user.agency_name}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        user.account_type === 'consultant'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user.account_type === 'consultant' ? 'Consultant' : 'Agency'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button onClick={() => handleApprove(user.id)}
                        className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5 rounded transition">
                        Approve
                      </button>
                      <button onClick={() => handleReject(user.id)}
                        className="bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium px-3 py-1.5 rounded transition">
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* AGENCIES TAB */}
      {activeTab === 'agencies' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-slate-800">All Agencies</h2>
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-sm text-gray-500 font-medium">
                <th className="px-6 py-3">Agency</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3 text-center">Users</th>
                <th className="px-6 py-3 text-center">Audits</th>
                <th className="px-6 py-3">Last Audit</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {agencies.map(agency => (
                <tr key={agency.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-slate-800">{agency.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{agency.email}</td>
                  <td className="px-6 py-4 text-center text-sm">{agency.total_users}</td>
                  <td className="px-6 py-4 text-center text-sm">{agency.total_audits}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {agency.last_audit_date ? new Date(agency.last_audit_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      agency.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>{agency.is_active ? 'Active' : 'Disabled'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => handleToggle(agency.id, agency.is_active)}
                      className={`text-sm font-medium px-3 py-1 rounded ${
                        agency.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                      }`}>
                      {agency.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AUDITS TAB */}
      {activeTab === 'audits' && (
        <div>
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Agency</label>
              <select value={auditFilters.agencyId}
                onChange={e => setAuditFilters({ ...auditFilters, agencyId: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Agencies</option>
                {agencies.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Client Agency</label>
              <input value={auditFilters.clientAgency}
                onChange={e => setAuditFilters({ ...auditFilters, clientAgency: e.target.value })}
                placeholder="Filter by client agency..."
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sort</label>
              <select value={auditFilters.sortBy}
                onChange={e => setAuditFilters({ ...auditFilters, sortBy: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="score_desc">Highest Score</option>
                <option value="score_asc">Lowest Score</option>
                <option value="agency">By Agency</option>
              </select>
            </div>
            <button onClick={loadAudits}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition">
              Apply Filters
            </button>
          </div>

          {/* Audit Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-500 font-medium">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Agency</th>
                  <th className="px-4 py-3">Patient</th>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3 text-center">Score</th>
                  <th className="px-4 py-3 text-center">Critical</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {audits.length === 0 ? (
                  <tr><td colSpan="8" className="px-6 py-8 text-center text-gray-400">No audits found</td></tr>
                ) : audits.map(audit => (
                  <tr key={audit.id} className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/audits/${audit.id}`)}>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(audit.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <p className="font-medium text-slate-800">{audit.client_agency_name || audit.agency_name}</p>
                      {audit.client_agency_name && audit.client_agency_name !== audit.agency_name && (
                        <p className="text-xs text-gray-400">by {audit.agency_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <p className="text-slate-700">{audit.patient_full_name || '—'}</p>
                      {audit.patient_reference && (
                        <p className="text-xs text-gray-400">{audit.patient_reference}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-[180px] truncate">
                      {audit.original_filename || '—'}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {audit.compliance_score != null ? (
                        <span className={`font-bold ${
                          audit.compliance_score >= 80 ? 'text-green-600' :
                          audit.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{audit.compliance_score}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-red-600 font-semibold">{audit.critical_count || 0}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        audit.status === 'completed' ? 'bg-green-100 text-green-700' :
                        audit.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>{audit.status}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-blue-600 hover:text-blue-800 text-sm font-medium">View &rarr;</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
